import { multi, withDependencies, optional, named } from '@wix/thunderbolt-ioc'
import type {
	IPageWillMountHandler,
	ServiceProvider,
	IPropsStore,
	IStructureAPI,
	Experiments,
	IPageServicesRegistrar,
	IPageWillUnmountHandler,
} from '@wix/thunderbolt-symbols'
import {
	ServiceProviderSymbol,
	Props,
	StructureAPI,
	SignalsPageServiceProviderSymbol,
	contextIdSymbol,
	ExperimentsSymbol,
	PageFeatureConfigSymbol,
	pageIdSym,
	SiteFeatureConfigSymbol,
	SiteServicesManagerAdapterSymbol,
} from '@wix/thunderbolt-symbols'
import {
	createServicesManagerFromProviders,
	setupExternalServices,
	getNoopServices,
} from '@wix/viewer-services-manager'
import type { ServiceRegistrarPageConfig, ServiceRegistrarSiteConfig } from './types'
import { name } from './symbols'
import type { ServicesManager } from '@wix/services-manager/types'
import type { IExternalServicesLoader } from 'feature-external-services'
import { ExternalServicesLoaderSymbol } from 'feature-external-services'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export const PageServiceManager = withDependencies<IPageWillMountHandler>(
	[
		multi(ServiceProviderSymbol),
		SignalsPageServiceProviderSymbol,
		StructureAPI,
		Props,
		ExperimentsSymbol,
		pageIdSym,
		contextIdSymbol,
		named(SiteFeatureConfigSymbol, name),
		optional(SiteServicesManagerAdapterSymbol),
		optional(ExternalServicesLoaderSymbol),
	],
	(
		pageServiceProviders: Array<ServiceProvider>,
		signalsServiceProvider: ServiceProvider,
		structureAPI: IStructureAPI,
		propsStore: IPropsStore,
		experiments: Experiments,
		pageId: string,
		contextId: string,
		siteConfig: ServiceRegistrarSiteConfig,
		siteServicesManagerAdapter?: ServicesManager,
		externalServicesLoader?: IExternalServicesLoader
	): IPageWillMountHandler & IPageServicesRegistrar => {
		const additionalServices = [signalsServiceProvider]
		const services = [...pageServiceProviders, ...additionalServices, ...getNoopServices()]
		const servicesManager = createServicesManagerFromProviders(services, siteServicesManagerAdapter)
		const isBuilder = siteConfig.isBuilderComponentModel
		const isServicesInfraOpen = isExperimentOpen(experiments, 'specs.thunderbolt.servicesInfra') || isBuilder
		const isDynamicPageServiceManagerExperiment = isExperimentOpen(
			experiments,
			'specs.thunderbolt.dynamicPageServiceManager'
		)
		const wrapperContextId = isDynamicPageServiceManagerExperiment ? contextId : pageId

		return {
			name: 'pageServiceManagerPageWillMount',
			async pageWillMount() {
				// Load and add external services if available
				await setupExternalServices(servicesManager, externalServicesLoader)

				if (isServicesInfraOpen) {
					const wrapperId =
						pageId === 'masterPage' ? 'masterPage' : structureAPI.getPageWrapperComponentId(pageId, wrapperContextId)
					propsStore.update({ [wrapperId]: { servicesManager } })
				}
			},
			get: () => servicesManager,
		}
	}
)

export const PageServiceManagerDs = withDependencies<IPageWillMountHandler>(
	[
		named(PageFeatureConfigSymbol, name),
		multi(ServiceProviderSymbol),
		SignalsPageServiceProviderSymbol,
		StructureAPI,
		Props,
		contextIdSymbol,
		ExperimentsSymbol,
		named(SiteFeatureConfigSymbol, name),
		optional(SiteServicesManagerAdapterSymbol),
		optional(ExternalServicesLoaderSymbol),
	],
	(
		{ isPopup }: ServiceRegistrarPageConfig,
		pageServiceProviders: Array<ServiceProvider>,
		signalsServiceProvider: ServiceProvider,
		structureAPI: IStructureAPI,
		propsStore: IPropsStore,
		contextId: string,
		experiments: Experiments,
		siteConfig: ServiceRegistrarSiteConfig,
		siteServicesManagerAdapter?: ServicesManager,
		externalServicesLoader?: IExternalServicesLoader
	): IPageWillMountHandler & IPageWillUnmountHandler & IPageServicesRegistrar => {
		const isBuilder = siteConfig.isBuilderComponentModel
		const isServicesInfraOpen = isExperimentOpen(experiments, 'specs.thunderbolt.servicesInfra') || isBuilder
		if (!isServicesInfraOpen) {
			return {
				name: 'pageServiceManagerPageWillMount',
				pageWillMount() {},
				pageWillUnmount() {},
				get: () => createServicesManagerFromProviders([]),
			}
		}
		const additionalServices = [signalsServiceProvider]

		const services = [...additionalServices, ...pageServiceProviders, ...getNoopServices()]
		const servicesManager = createServicesManagerFromProviders(services, siteServicesManagerAdapter)

		let unsubscribeFromExternalServices: (() => void) | undefined

		return {
			name: 'pageServiceManagerPageWillMount',
			async pageWillMount(pageId: string) {
				// Setup external services subscription and load pending services
				unsubscribeFromExternalServices = await setupExternalServices(servicesManager, externalServicesLoader)

				if (isServicesInfraOpen) {
					if (pageId === 'masterPage') {
						propsStore.update({ masterPage: { servicesManager } })
					} else {
						const wrapperId = isPopup ? structureAPI.getPageWrapperComponentId(pageId, pageId) : contextId
						propsStore.update({ [wrapperId]: { servicesManager } })
					}
				}
			},
			pageWillUnmount() {
				// Cleanup: unsubscribe from external services
				unsubscribeFromExternalServices?.()
			},
			get: () => servicesManager,
		}
	}
)
